import { useState } from 'react';
import { Activity, Globe, Eye, MousePointer, DollarSign, Target, ChevronRight, Filter, Search } from 'lucide-react';
import { fmt } from '../utils/helpers';

interface DomainPerformanceProps {
    data: any;
    setDomainDetailModal: (domain: any) => void;
}

export function DomainPerformance({ data, setDomainDetailModal }: DomainPerformanceProps) {
    const [sortBy, setSortBy] = useState<'revenue' | 'impressions' | 'ctr' | 'ecpm'>('revenue');
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [searchTerm, setSearchTerm] = useState('');

    const domains = data.charts?.by_domain || [];

    // Filtrar e ordenar domínios
    const filteredDomains = domains
        .filter((d: any) => d.domain.toLowerCase().includes(searchTerm.toLowerCase()))
        .sort((a: any, b: any) => b[sortBy] - a[sortBy]);

    // Calcular estatísticas gerais
    const totalRevenue = domains.reduce((sum: number, d: any) => sum + d.revenue, 0);
    const avgCTR = domains.length > 0
        ? domains.reduce((sum: number, d: any) => sum + d.ctr, 0) / domains.length
        : 0;


    if (domains.length === 0) {
        return (
            <div className="rounded-xl bg-white p-12 shadow-sm border border-gray-200 text-center">
                <Globe className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Nenhum Domínio</h3>
                <p className="text-sm text-gray-500">Não há dados de domínios disponíveis</p>
            </div>
        );
    }

    return (
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-5 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    {/* Título e Stats */}
                    <div>
                        <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-2">
                            <Activity className="h-6 w-6 text-indigo-600" />
                            Performance por Domínio
                        </h3>
                        <div className="flex items-center gap-4 text-sm">
                            <span className="text-gray-600">
                                <span className="font-semibold text-indigo-600">{domains.length}</span> domínios
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-600">
                                <span className="font-semibold text-emerald-600">{fmt.currency(totalRevenue)}</span> total
                            </span>
                            <span className="text-gray-300">•</span>
                            <span className="text-gray-600">
                                CTR médio: <span className="font-semibold text-purple-600">{fmt.percent(avgCTR)}</span>
                            </span>
                        </div>
                    </div>

                    {/* Controles */}
                    <div className="flex flex-wrap gap-2">
                        {/* Busca */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Buscar domínio..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                            />
                        </div>

                        {/* Toggle View */}
                        <div className="flex gap-1 p-1 bg-white border border-gray-200 rounded-lg">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    viewMode === 'grid' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
                                    viewMode === 'list' ? 'bg-indigo-500 text-white' : 'text-gray-600 hover:text-gray-900'
                                }`}
                            >
                                Lista
                            </button>
                        </div>

                        {/* Sort */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <select
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value as any)}
                                className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 appearance-none bg-white cursor-pointer"
                            >
                                <option value="revenue">Receita</option>
                                <option value="impressions">Impressões</option>
                                <option value="ctr">CTR</option>
                                <option value="ecpm">eCPM</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="p-6">
                {filteredDomains.length === 0 ? (
                    <div className="text-center py-12">
                        <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                        <p className="text-sm text-gray-500">Nenhum domínio encontrado com "{searchTerm}"</p>
                    </div>
                ) : (
                    <>
                        {/* Grid View */}
                        {viewMode === 'grid' && (
                            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {filteredDomains.map((domain: any, index: number) => {

                                    return (
                                        <div
                                            key={index}
                                            onClick={() => setDomainDetailModal(domain)}
                                            className="group relative bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-indigo-300 hover:shadow-xl transition-all cursor-pointer"
                                        >
                                            {/* Header */}
                                            <div className="flex items-start justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="relative">
                                                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                                                            <Globe className="h-6 w-6 text-indigo-600" />
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className="text-xs font-bold text-gray-400">#{index + 1}</span>
                                                    </div>
                                                </div>
                                                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all" />
                                            </div>

                                            {/* Domain Name */}
                                            <h4 className="font-bold text-gray-900 text-sm mb-2 truncate" title={domain.domain}>
                                                {domain.domain}
                                            </h4>

                                            {/* Revenue */}
                                            <div className="mb-4">
                                                <p className="text-2xl font-bold text-indigo-600">{fmt.currency(domain.revenue)}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                                                        <div
                                                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full transition-all duration-500"
                                                            style={{ width: `${(domain.revenue / totalRevenue) * 100}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs text-gray-500 font-medium">
                                                        {((domain.revenue / totalRevenue) * 100).toFixed(1)}%
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Metrics Grid */}
                                            <div className="grid grid-cols-2 gap-2 mb-3">
                                                <div className="bg-blue-50 rounded-lg p-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <Eye className="h-3 w-3 text-blue-600" />
                                                        <span className="text-xs text-blue-900 font-medium">Impressões</span>
                                                    </div>
                                                    <p className="text-sm font-bold text-blue-700">{fmt.number(domain.impressions)}</p>
                                                </div>

                                                <div className="bg-purple-50 rounded-lg p-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <MousePointer className="h-3 w-3 text-purple-600" />
                                                        <span className="text-xs text-purple-900 font-medium">Cliques</span>
                                                    </div>
                                                    <p className="text-sm font-bold text-purple-700">{fmt.number(domain.clicks)}</p>
                                                </div>

                                                <div className="bg-orange-50 rounded-lg p-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <Target className="h-3 w-3 text-orange-600" />
                                                        <span className="text-xs text-orange-900 font-medium">CTR</span>
                                                    </div>
                                                    <p className="text-sm font-bold text-orange-700">{fmt.percent(domain.ctr)}</p>
                                                </div>

                                                <div className="bg-emerald-50 rounded-lg p-2.5">
                                                    <div className="flex items-center gap-1.5 mb-1">
                                                        <DollarSign className="h-3 w-3 text-emerald-600" />
                                                        <span className="text-xs text-emerald-900 font-medium">eCPM</span>
                                                    </div>
                                                    <p className="text-sm font-bold text-emerald-700">{fmt.currency(domain.ecpm)}</p>
                                                </div>
                                            </div>

                                            {/* Footer Stats */}
                                            <div className="flex items-center justify-between text-xs pt-3 border-t border-gray-200">
                                                <div className="flex items-center gap-1 text-gray-600">
                                                    <Activity className="h-3 w-3" />
                                                    <span>{domain.ad_blocks?.length || 0} blocos</span>
                                                </div>
                                                <div className="flex items-center gap-1 text-gray-600">
                                                    <Globe className="h-3 w-3" />
                                                    <span>{domain.countries?.filter((c: any) => c.impressions > 0).length || 0} países</span>
                                                </div>
                                                <div className="font-semibold text-indigo-600">
                                                    PMR {fmt.percent(domain.pmr)}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* List View */}
                        {viewMode === 'list' && (
                            <div className="space-y-3">
                                {filteredDomains.map((domain: any, index: number) => {

                                    return (
                                        <div
                                            key={index}
                                            onClick={() => setDomainDetailModal(domain)}
                                            className="group bg-white border-2 border-gray-200 rounded-xl p-4 hover:border-indigo-300 hover:shadow-lg transition-all cursor-pointer"
                                        >
                                            <div className="flex items-center gap-4">
                                                {/* Rank & Icon */}
                                                <div className="flex items-center gap-3">
                                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                                                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-orange-500 text-white' :
                                                            index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-400 text-gray-700' :
                                                                index === 2 ? 'bg-gradient-to-br from-orange-300 to-orange-400 text-white' :
                                                                    'bg-gray-100 text-gray-600'
                                                    }`}>
                                                        {index + 1}
                                                    </div>
                                                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center">
                                                        <Globe className="h-5 w-5 text-indigo-600" />
                                                    </div>
                                                </div>

                                                {/* Domain Info */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <h4 className="font-bold text-gray-900 text-sm truncate" title={domain.domain}>
                                                            {domain.domain}
                                                        </h4>
                                                    </div>
                                                    <p className="text-xl font-bold text-indigo-600">{fmt.currency(domain.revenue)}</p>
                                                </div>

                                                {/* Metrics */}
                                                <div className="hidden md:flex items-center gap-6">
                                                    <div className="text-center">
                                                        <p className="text-xs text-gray-500 mb-1">Impressões</p>
                                                        <p className="text-sm font-bold text-gray-900">{fmt.number(domain.impressions)}</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-xs text-gray-500 mb-1">CTR</p>
                                                        <p className="text-sm font-bold text-purple-600">{fmt.percent(domain.ctr)}</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-xs text-gray-500 mb-1">eCPM</p>
                                                        <p className="text-sm font-bold text-orange-600">{fmt.currency(domain.ecpm)}</p>
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-xs text-gray-500 mb-1">PMR</p>
                                                        <p className="text-sm font-bold text-emerald-600">{fmt.percent(domain.pmr)}</p>
                                                    </div>
                                                </div>

                                                {/* Arrow */}
                                                <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-indigo-600 group-hover:translate-x-1 transition-all flex-shrink-0" />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}